<template>
  <v-container>
    <div class="container">
      <h1 class="subs-title">{{ adminText('organizationAll.theater_subscribers_list_8ac9fc47', 'Theater Subscribers List') }}</h1>
      <p class="page-description">{{ adminText('organizationAll.manage_your_subscribers_and_seats_3c54a0f6', 'Manage your subscribers and seats.') }}</p>
      <v-layout row wrap class="mb-4 justify-space-between">
        <v-flex xs12 sm4 class="pr-3">
          <v-text-field
            v-model="searchTerm"
            append-icon="search"
            :label="adminText('organizationAll.search_by_name_email_or_phone_5c5f3caa', 'Search by name, email, or phone')"
            single-line
            hide-details
          />
        </v-flex>
        <v-flex xs12 sm4 class="pr-3">
          <v-select
            v-model="selectedSeason"
            :items="organizationSentinelOptions(seasonFilterOptions, 'text')"
            :label="adminText('organizationAll.season_62255f56', 'Season')"
            item-text="text"
            item-value="value"
            hide-details
          />
        </v-flex>
        <v-flex xs12 sm4>
          <v-select
            v-model="selectedSubscriptionId"
            :items="organizationSentinelOptions(subscriptionFilterOptions, 'text')"
            item-text="text"
            item-value="value"
            :label="adminText('organizationAll.subscription_plan_b07550a7', 'Subscription plan')"
            clearable
            hide-details
          />
        </v-flex>
        <v-flex xs12 class="subscriber-actions">
          <a
            v-if="exportToken"
            :href="exportUrl"
            download
            class="export-link"
          >
            <main-button flat color="primary">
              <v-icon left small>file_upload</v-icon>{{ adminText('organizationAll.export_subscribers_cd6c43e1', 'EXPORT SUBSCRIBERS') }}
            </main-button>
          </a>
          <main-button depressed @click="navigateToMessageSubscribers">
            <v-icon left small>email</v-icon>{{ adminText('organizationAll.message_subscribers_f11ec9c9', 'MESSAGE SUBSCRIBERS') }}
          </main-button>
        </v-flex>
        <v-flex xs12 class="mt-4">
          <v-data-table
            :key="subscribers.length"
            :headers="organizationHeaders(headers)"
            :items="subscribers"
            :items-per-page="5"
            :loading="loading"
            :no-data-text="organizationText(noDataText)"
            class="elevation-1 compact-table"
          >
            <template v-slot:items="props">
              <tr @click="viewSubscriber(props.item)">
                <td>
                  <button
                    type="button"
                    class="row-email-action"
                    :disabled="!props.item.active || !props.item.customerEmail"
                    :aria-label="`${adminText('organizationAll.send_email_to_b25c4f44', 'Send email to ')}${props.item.customerName}`"
                    @click.stop="navigateToMessageSubscriber(props.item)"
                  >
                    {{ adminText('organizationAll.send_email_619a901e', 'Send email') }}
                  </button>
                </td>
                <td>{{ props.item.customerName }}</td>
                <td>{{ props.item.subscriptionName }}</td>
                <td>{{ props.item.validFrom }}</td>
                <td>{{ props.item.validTo }}</td>
                <td>{{ formatTicketLocation(props.item.ticketLocation) }}</td>
                <td>{{ organizationText(props.item.status) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </div>

    <!-- View Subscriber Modal -->
    <v-dialog v-model="showViewModal" max-width="900px" content-class="subscriber-detail-modal">
      <v-card
        v-if="selectedSubscriber && Object.keys(selectedSubscriber).length > 0"
        :key="selectedSubscriber.id"
      >
        <div class="pa-4 pb-0">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="subscriber-name">{{ selectedSubscriber.customerName }}</span>
            <v-btn class="close-modal-btn" flat icon @click="showViewModal = false">
              <v-icon>close</v-icon>
            </v-btn>
          </div>
          <p class="subscriber-details">
            {{ selectedSubscriber.customerEmail }}
            <template v-if="selectedSubscriber.customerPhone">
              • {{ selectedSubscriber.customerPhone }}
            </template>
            <template v-if="selectedSubscriber.customerAddress">
              • {{ selectedSubscriber.customerAddress }}
            </template>
          </p>

          <div class="subscription-details-container">
            <v-layout row align-center>
              <v-flex>
                <h3 class="subscription-name">
                  {{ selectedSubscriber.subscriptionName }}
                </h3>
                <p class="subscription-ticket-location-details">
                  {{ selectedSubscriber.ticketLocationDetails }}
                </p>
              </v-flex>
              <v-flex shrink class="text-right">
                <p class="subscription-valid-from-to">
                  {{ selectedSubscriber.validFrom }} – {{ selectedSubscriber.validTo }}
                </p>
              </v-flex>
            </v-layout>
          </div>
        </div>

        <v-tabs
          v-model="currentTab"
          hide-slider
          background-color="transparent"
          class="tabs-container"
        >
          <v-tab
            value="subscription-events-tab"
            href="#subscription-events-tab"
            class="tab-container subscription-events-tab"
            style="color: white"
          >
            {{ adminText('organizationAll.subscription_events_90a69662', 'Subscription Events') }}
          </v-tab>
          <v-tab
            v-if="!isSelectedSubscriberGeneralAdmission"
            value="floor-plan-tab"
            href="#floor-plan-tab"
            class="tab-container floor-plan-tab"
            style="color: white"
          >
            {{ adminText('organizationAll.floor_plan_19600557', 'Floor Plan') }}
          </v-tab>

          <div v-if="!isSelectedSubscriberGeneralAdmission" class="release-seats-btn-container">
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                class="release-seats-btn"
                elevation="0"
                depressed
                @click="releaseSeatForAllUpcomingPerformances(selectedSubscriber.id)"
              >
                {{ adminText('organizationAll.release_seat_for_all_44f8bfb6', 'Release seat for all') }}
              </v-btn>
              <span>{{ adminText('organizationAll.free_up_this_seat_for_all_upcoming_performanc_4e6ab39f', 'Free up this seat for all upcoming performances.') }}</span>
            </v-tooltip>
          </div>
        </v-tabs>

        <v-card-text class="pa-0 modal-card-text-content">
          <v-tabs-items v-model="currentTab">
            <v-tab-item id="subscription-events-tab">
              <div class="pa-4">
                <div v-if="detailedEventsLoading" class="text-center py-8">
                  <v-progress-circular indeterminate color="primary" />
                  <p class="mt-2">{{ adminText('organizationAll.loading_events_f691a435', 'Loading events...') }}</p>
                </div>
                <div v-else-if="organizationText(detailedEventsError)" class="text-center pa-8 empty-state-content">
                  <v-icon size="64" color="red lighten-2">alert-circle-outline</v-icon>
                  <p class="mt-4 mb-1 title font-weight-regular">{{ adminText('organizationAll.error_loading_events_84147f46', 'Error loading events.') }}</p>
                  <p class="caption grey--text mb-4">{{ organizationText(detailedEventsError) }}</p>
                  <v-btn
                    text
                    color="primary"
                    @click="fetchDetailedSubscriptionEvents(selectedSubscriber.id)"
                  >
                    <v-icon left small>refresh</v-icon>
                    {{ adminText('organizationAll.try_again_cef2fe09', 'Try Again') }}
                  </v-btn>
                </div>
                <div
                  v-else-if="detailedSubscriptionEvents && detailedSubscriptionEvents.length > 0"
                >
                  <v-list dense class="py-0">
                    <div
                      v-for="(event, index) in detailedSubscriptionEvents"
                      :key="event.id || 'loop-event-' + index"
                    >
                      <v-list-tile class="event-list-item px-0">
                        <v-list-tile-content>
                          <v-list-tile-title>
                            <v-tooltip bottom>
                              <div slot="activator">
                                <span class="event-date grey--text text--darken-1">{{
                                  formatEventDate(event.date)
                                }}</span>
                                <span class="event-name ml-6">{{ event.name }}</span>
                              </div>
                              <span>{{ organizationText(getEventStatusMessage(event).message) }}</span>
                            </v-tooltip>
                          </v-list-tile-title>
                        </v-list-tile-content>

                        <v-list-tile-action class="my-0 v-list__tile__action--stack d-flex">
                          <div
                            v-if="canReleaseEventSeat(event)"
                            @click="showReleaseSeatConfirmation(event)"
                          >
                            <v-tooltip bottom>
                              <v-btn slot="activator" flat class="release-seat-btn">
                                {{ adminText('organizationAll.release_seat_bf8fdb7f', 'RELEASE SEAT') }}
                              </v-btn>
                              <span>{{
                                organizationText(getEventStatusIcon(
                                  event.interactionStatus,
                                  event.status,
                                  event.date,
                                ).tooltip)
                              }}</span>
                            </v-tooltip>
                          </div>
                          <div
                            v-else-if="isEventSeatReleasedAndSoldToOther(event)"
                            class="caption grey--text d-flex align-center pa-2"
                          >
                            <v-tooltip bottom>
                              <v-icon
                                slot="activator"
                                :color="
                                  getEventStatusIcon(
                                    event.interactionStatus,
                                    event.status,
                                    event.date,
                                  ).color
                                "
                              >
                                {{
                                  getEventStatusIcon(
                                    event.interactionStatus,
                                    event.status,
                                    event.date,
                                  ).icon
                                }}
                              </v-icon>
                              <span>{{
                                organizationText(getEventStatusIcon(
                                  event.interactionStatus,
                                  event.status,
                                  event.date,
                                ).tooltip)
                              }}</span>
                            </v-tooltip>
                          </div>
                          <div
                            v-else-if="canReassignEventSeat(event)"
                            class="caption grey--text d-flex align-center pa-2"
                            @click="reassignSeat(event.purchaseId, event.id)"
                          >
                            <v-tooltip bottom>
                              <v-icon slot="activator" color="green darken-1">
                                {{
                                  getEventStatusIcon(
                                    event.interactionStatus,
                                    event.status,
                                    event.date,
                                  ).icon
                                }}
                              </v-icon>
                              <span>{{ adminText('organizationAll.reassign_seat_9d532a48', 'Reassign Seat') }}</span>
                            </v-tooltip>
                          </div>
                          <div v-else class="caption grey--text d-flex align-center pa-2">
                            <v-tooltip bottom>
                              <v-icon
                                slot="activator"
                                :color="
                                  getEventStatusIcon(
                                    event.interactionStatus,
                                    event.status,
                                    event.date,
                                  ).color
                                "
                              >
                                {{
                                  getEventStatusIcon(
                                    event.interactionStatus,
                                    event.status,
                                    event.date,
                                  ).icon
                                }}
                              </v-icon>
                              <span>{{
                                organizationText(getEventStatusIcon(
                                  event.interactionStatus,
                                  event.status,
                                  event.date,
                                ).tooltip)
                              }}</span>
                            </v-tooltip>
                          </div>
                        </v-list-tile-action>
                      </v-list-tile>
                      <v-divider v-if="index < detailedSubscriptionEvents.length - 1" />
                    </div>
                  </v-list>
                </div>
                <div v-else class="text-center pa-8 empty-state-content">
                  <v-icon size="64" color="grey lighten-2">calendar-multiple-check</v-icon>
                  <p class="mt-4 mb-1 title font-weight-regular">
                    {{ adminText('organizationAll.no_events_have_been_added_to_this_subscriptio_9ab071b1', 'No events have been added to this subscription plan yet.') }}
                  </p>
                  <p class="caption grey--text mb-4">
                    {{ adminText('organizationAll.performances_will_appear_here_once_they_are_l_ed9061fc', 'Performances will appear here once they are linked based on') }}<br>{{ adminText('organizationAll.their_date_and_subscription_settings_6e1d7609', 'their date and subscription settings.') }}
                  </p>
                  <!-- <v-btn
                    color="primary"
                    class="text-none action-button"
                    elevation="0"
                    @click="navigateToCreateEvent"
                  >
                    Create Event
                  </v-btn> -->
                </div>
              </div>
            </v-tab-item>

            <v-tab-item id="floor-plan-tab">
              <div class="pa-4 floor-plan-tab-content">
                <div v-if="floorPlanLoading" class="text-center py-8">
                  <v-progress-circular indeterminate color="primary" />
                  <p class="mt-2">{{ adminText('organizationAll.loading_floor_plan_d7f567c8', 'Loading floor plan...') }}</p>
                </div>
                <div v-else-if="organizationText(floorPlanError)" class="text-center pa-8 empty-state-content">
                  <v-icon size="64" color="red lighten-2">alert-circle-outline</v-icon>
                  <p class="mt-4 mb-1 title font-weight-regular">{{ adminText('organizationAll.error_loading_floor_plan_ac895cb0', 'Error loading floor plan.') }}</p>
                  <p class="caption grey--text mb-4">{{ organizationText(floorPlanError) }}</p>
                  <v-btn
                    color="primary"
                    class="text-none action-button"
                    elevation="0"
                    @click="fetchFloorPlanData"
                  >
                    <v-icon left small>refresh</v-icon>
                    {{ adminText('organizationAll.try_again_cef2fe09', 'Try Again') }}
                  </v-btn>
                </div>
                <div v-else-if="floorPlanData && floorPlanData.venueImageUrl">
                  <div class="d-flex justify-end mb-1">
                    <v-btn
                      color="primary"
                      disabled
                      class="text-none action-button"
                      elevation="0"
                      @click="changeSeat"
                    >
                      {{ adminText('organizationAll.change_seat_aee39fa3', 'Change Seat') }}
                    </v-btn>
                  </div>
                  <div class="floor-plan-display-container">
                    <div ref="floorPlanObjectWrapper" class="floor-plan-object-wrapper">
                      <object
                        :id="'subscriberFloorPlanSvg-' + selectedSubscriber.id"
                        ref="subscriberFloorPlanSvgObject"
                        :data="floorPlanData.venueImageUrl"
                        type="image/svg+xml"
                        style="width: 100%"
                        @load="onFloorPlanSvgLoad"
                      />
                    </div>
                    <div class="zoom-controls">
                      <v-btn
                        color="primary"
                        icon
                        :disabled="floorPlanZoomLevel >= 2.5"
                        @click="zoomFloorPlanIn"
                      >
                        <v-icon color="white">add</v-icon>
                      </v-btn>
                      <v-btn
                        color="primary"
                        icon
                        :disabled="floorPlanZoomLevel <= 0.5"
                        @click="zoomFloorPlanOut"
                      >
                        <v-icon>remove</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center pa-8 empty-state-content">
                  <v-icon size="64" color="grey lighten-2">map-marker-off-outline</v-icon>
                  <p class="mt-4 mb-1 title font-weight-regular">{{ adminText('organizationAll.floor_plan_not_available_01405dd0', 'Floor plan not available.') }}</p>
                  <p class="caption grey--text mb-4">
                    {{ adminText('organizationAll.the_floor_plan_for_this_subscription_could_no_7707c626', 'The floor plan for this subscription could not be loaded.') }}
                  </p>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 grey lighten-5">
          <v-tooltip top color="rgba(51, 51, 51, 0.80)" max-width="10rem">
            <v-btn
              slot="activator"
              color="primary"
              class="text-none action-button-outline"
              elevation="0"
              outlined
              :disabled="selectedSubscriber.active"
              @click="renewSubscription(selectedSubscriber.id)"
            >
              {{ adminText('organizationAll.renew_subscription_6f959714', 'Renew Subscription') }}
            </v-btn>
            <span>
              {{ selectedSubscriber.active ? adminText('organizationAll.subscription_is_active_fc1d2e58', 'Subscription is active') : adminText('organizationAll.reactivate_subscription_a3a80e33', 'Reactivate subscription') }}
            </span>
          </v-tooltip>
          <v-spacer />
          <!-- <v-btn
            color="primary"
            elevation="0"
            class="text-none action-button"
            :loading="resendingEmail"
            @click="resendSubscriptionEmail(selectedSubscriber.id)"
          >
            <v-icon left small>email</v-icon>
            Resend Email
          </v-btn> -->
          <v-btn
            color="primary"
            elevation="0"
            class="text-none action-button"
            :loading="printingSubscription"
            style="margin-left: 12px"
            @click="printSubscription(selectedSubscriber.id)"
          >
            <v-icon left small>printer</v-icon>
            {{ adminText('organizationAll.print_subscription_1b577b23', 'Print Subscription') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">{{ adminText('organizationAll.delete_subscriber_eab0e441', 'Delete Subscriber') }}</v-card-title>
        <v-card-text> {{ adminText('organizationAll.are_you_sure_you_want_to_delete_this_subscrib_10e2d6f3', 'Are you sure you want to delete this subscriber?') }} </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            class="text-none action-button-outline"
            @click="showDeleteDialog = false"
          >
            {{ adminText('organizationAll.cancel_77dfd213', 'Cancel') }}
          </v-btn>
          <v-btn color="red darken-1" class="text-none action-button" @click="confirmDelete">
            {{ adminText('organizationAll.delete_f6fdbe48', 'Delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Release Seat Confirmation Dialog -->
    <v-dialog v-model="showReleaseSeatDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">{{ adminText('organizationAll.release_seat_f2a00b29', 'Release Seat') }}</v-card-title>
        <v-card-text>
          <p>{{ adminText('organizationAll.are_you_sure_you_want_to_release_this_seat_b3e43917', 'Are you sure you want to release this seat?') }}</p>
          <p class="mt-2">
            <strong>{{ adminText('organizationAll.this_action_will_70a2220d', 'This action will:') }}</strong>
          </p>
          <ul class="mt-2">
            <li>{{ adminText('organizationAll.mark_the_seat_as_available_for_purchase_ba390f4c', 'Mark the seat as available for purchase') }}</li>
            <li>
              {{ adminText('organizationAll.remove_the_subscriber_s_access_to_d7e6a048', 'Remove the subscriber\'s access to') }} <strong>{{ releaseSeatEventName }}</strong>
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            class="text-none action-button-outline"
            @click="showReleaseSeatDialog = false"
          >
            {{ adminText('organizationAll.cancel_77dfd213', 'Cancel') }}
          </v-btn>
          <v-btn color="red darken-1" class="text-none action-button" @click="confirmReleaseSeat">
            {{ adminText('organizationAll.release_seat_f2a00b29', 'Release Seat') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import {
  getTheaterSubscriptions,
  searchTheaterSubscriptionPurchases,
  getTheaterSubscriptionEventsForPurchase,
  getFloorPlanForSubscriptionPurchase,
  releaseSeatForSubscriptionPurchaseEvent,
  reassignSeatForSubscriptionPurchaseEvent,
  releaseSeatForAllUpcomingPerformances,
  resendTheaterSubscriptionConfirmation,
} from '@/graphql/theater-subscriptions.graphql.js'
import { deleteTheaterSubscriptionPurchase } from '@/graphql/theater-subscriptions.graphql.js'
import moment from 'moment'
import axios from 'axios'
import { serverURL } from '@/utils'
import { getTheaterSubscribersExportToken } from '@/http'

const SeatState = {
  AVAILABLE: 'AVAILABLE',
  HOLD: 'HOLD',
  SOLD: 'SOLD',
  DISABLED: 'DISABLED',
}

const SubscriptionEventInteractionStatus = {
  SUBSCRIPTION_RESERVED: 'SUBSCRIPTION_RESERVED',
  EVENT_CANCELLED: 'EVENT_CANCELLED',
  RELEASED_AND_AVAILABLE: 'RELEASED_AND_AVAILABLE',
  SCANNED: 'SCANNED',
  MISSED: 'MISSED',
  ATTENDING: 'ATTENDING',
  RELEASED_AND_SOLD_TO_OTHER: 'RELEASED_AND_SOLD_TO_OTHER',
  MISSED_UNTRACKABLE_SCAN: 'MISSED_UNTRACKABLE_SCAN',
  SEAT_ON_HOLD: 'SEAT_ON_HOLD',
  SEAT_NOT_AVAILABLE: 'SEAT_NOT_AVAILABLE',
}

const THEATER_SUBSCRIPTION_GA_TICKET_LOCATION = 'Online Purchase'
const THEATER_SUBSCRIPTION_GA_DISPLAY_LABEL = 'Nenumerirano'

export default {
  mixins: [OrganizationLocalization],
  name: 'theater-subscriptions-list',
  components: {},
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      searchTerm: '',
      selectedSubscriptionId: '',
      selectedSeason: '',
      subscriberRequestId: 0,
      showViewModal: false,
      showDeleteDialog: false,
      showReleaseSeatDialog: false,
      selectedSubscriber: {},
      selectedTicketLocation: '',
      subscriberToDelete: null,
      releaseSeatEventName: '',
      releaseSeatPurchaseId: null,
      releaseSeatEventId: null,
      subscribers: [],
      subscriptions: [],
      currentTab: 'subscription-events-tab',
      detailedSubscriptionEvents: [],
      detailedEventsLoading: false,
      detailedEventsError: null,
      floorPlanData: null,
      floorPlanLoading: false,
      floorPlanError: null,
      floorPlanZoomLevel: 1,
      printingSubscription: false,
      resendingEmail: false,
      exportToken: null,
      SeatState: SeatState,
      SubscriptionEventInteractionStatus: SubscriptionEventInteractionStatus,
      headers: [
        { text: '', value: 'emailAction', sortable: false },
        { text: 'Customer Name', value: 'customerName' },
        { text: 'Subscription', value: 'subscriptionName' },
        { text: 'Valid From', value: 'validFrom' },
        { text: 'Valid To', value: 'validTo' },
        { text: 'Seat / Admission', value: 'ticketLocation' },
        { text: 'Status', value: 'status' },
      ],
      noDataText: 'No subscribers available',
      searchTimeout: null,
    }
  },
  computed: {
    exportUrl() {
      const base = `${serverURL}/files/theater-organizations/${this.organizationSlug}/subscribers/export/xlsx/${this.exportToken}`
      const planId = this.selectedSubscriptionId

      const params = new URLSearchParams()
      if (planId) params.set('subscriptionId', planId)
      if (this.selectedSeason) params.set('season', this.selectedSeason)

      return params.toString() ? `${base}?${params}` : base
    },
    seasonFilterOptions() {
      const seasons = [...new Set((this.subscriptions || []).map(this.getSeasonLabel).filter(Boolean))]

      return [{ text: 'All seasons', value: '' }, ...seasons.sort().reverse().map(value => ({ text: value, value }))]
    },
    subscriptionFilterOptions() {
      const baseOption = [{ text: 'All plans', value: '' }]
      const planOptions = (this.subscriptions || []).filter(subscription => !this.selectedSeason || this.getSeasonLabel(subscription) === this.selectedSeason).map(subscription => ({
        text: subscription.name,
        value: subscription.id,
      }))

      return [...baseOption, ...planOptions]
    },
    isSelectedSubscriberGeneralAdmission() {
      return this.isGeneralAdmissionSubscriber(this.selectedSubscriber)
    },
  },
  watch: {
    selectedSeason() {
      clearTimeout(this.searchTimeout)
      if (this.selectedSubscriptionId && !this.subscriptionFilterOptions.some(option => String(option.value) === String(this.selectedSubscriptionId))) {
        this.selectedSubscriptionId = ''

        return
      }
      this.fetchSubscribers()
    },
    searchTerm() {
      this.scheduleSubscriberFetch()
    },
    selectedSubscriptionId() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.fetchSubscribers()
    },
    currentTab(newTab) {
      console.log(
        '[Watcher] currentTab changed to:',
        newTab,
        'Selected Subscriber ID:',
        this.selectedSubscriber ? this.selectedSubscriber.id : 'N/A',
        'Current floorPlanData:',
        JSON.parse(JSON.stringify(this.floorPlanData)),
      )

      // Debugging the condition
      console.log(
        '[Watcher] currentTab - Value of newTab before check:"' +
          newTab +
          '" (length: ' +
          (newTab ? newTab.length : 'N/A') +
          ')',
      )
      if (typeof newTab === 'string') {
        console.log(
          '[Watcher] currentTab - Value of newTab.trim() before check:"' +
            newTab.trim() +
            '" (length: ' +
            newTab.trim().length +
            ')',
        )
      }

      if (
        typeof newTab === 'string' &&
        newTab.trim() === 'floor-plan-tab' &&
        this.selectedSubscriber &&
        this.selectedSubscriber.id &&
        !this.floorPlanData &&
        !this.isGeneralAdmissionSubscriber(this.selectedSubscriber)
      ) {
        console.log('[Watcher] currentTab: Conditions met, calling fetchFloorPlanData.')
        this.fetchFloorPlanData()
      } else {
        console.log('[Watcher] currentTab: Conditions NOT met. Details above.')
      }
    },
    selectedSubscriber(newSubscriber, oldSubscriber) {
      console.log(
        '[Watcher] selectedSubscriber changed. New:',
        newSubscriber ? newSubscriber.id : 'N/A',
        'Old:',
        oldSubscriber ? oldSubscriber.id : 'N/A',
        'Current Tab:',
        this.currentTab,
      )
      // If subscriber changes, and floor plan tab is active, refresh data
      if (
        newSubscriber &&
        newSubscriber.id &&
        this.currentTab === 'floor-plan-tab' &&
        !this.isGeneralAdmissionSubscriber(newSubscriber)
      ) {
        this.resetFloorPlanData() // Reset first
        this.fetchFloorPlanData()
      } else if (!newSubscriber || !newSubscriber.id) {
        this.resetFloorPlanData() // Clear if no subscriber
      }
    },
  },
  async created() {
    this.fetchInitialData()
    this.fetchExportToken()
  },
  beforeDestroy() {
    this.subscriberRequestId++
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    getSeasonLabel(plan) {
      if (!plan || !plan.valid_from) return ''
      const from = new Date(plan.valid_from).getFullYear()
      if (Number.isNaN(from)) return ''
      const to = plan.valid_to ? new Date(plan.valid_to).getFullYear() : from

      return Number.isNaN(to) || from === to ? `${from}` : `${from}/${to}`
    },
    async fetchExportToken() {
      try {
        const { data } = await getTheaterSubscribersExportToken(this.organizationSlug)
        this.exportToken = data && data.token ? data.token : null
      } catch (error) {
        // Export stays hidden rather than offering a link that would 401.
        this.exportToken = null
      }
    },
    navigateToMessageSubscribers() {
      this.$router.push({
        name: this.routes.theaterOrganization.theaterOrganizationTheaterSubscribersMessage,
        params: { organizationSlug: this.organizationSlug },
      })
    },
    navigateToMessageSubscriber(subscriber) {
      if (!subscriber.active || !String(subscriber.customerEmail || '').trim()) return
      this.$router.push({
        name: this.routes.theaterOrganization.theaterOrganizationTheaterSubscribersMessage,
        params: { organizationSlug: this.organizationSlug },
        query: { recipientId: String(subscriber.id) },
      })
    },
    navigateToCreateEvent() {
      this.goTo(this.routes.theaterOrganization.theaterOrganizationAddEvent)
    },
    async renewSubscription(purchaseId) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: require('@/graphql/theater-subscriptions.graphql.js')
            .renewTheaterSubscriptionPurchase,
          variables: {
            purchaseId: parseInt(purchaseId),
            organizationSlug: this.organizationSlug,
          },
        })
        if (data && data.renewTheaterSubscriptionPurchase) {
          if (this.$root && this.$root.$emit) {
            this.$root.$emit('show-snackbar', {
              message: 'Subscription renewed successfully.',
              color: 'success',
            })
          }
          await this.fetchSubscribersForAllSubscriptions()
        }
      } catch (e) {
        console.error('Failed to renew subscription', e)
        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: this.organizationMessage('organizationAll.failed_to_renew_subscription_value0_adea8554', 'Failed to renew subscription: {{value0}}', { value0: e.message || this.organizationMessage('organizationAll.unknown_error_e5fd9aa2', 'Unknown error') }),
            color: 'error',
          })
        }
      }
    },
    async fetchInitialData() {
      await this.fetchSubscriptions()
      await this.fetchSubscribers()
    },
    async fetchSubscriptions() {
      this.loading = true
      try {
        const response = await this.$apollo.query({
          query: getTheaterSubscriptions,
          variables: {
            organizationSlug: this.organizationSlug,
          },
          fetchPolicy: 'network-only',
        })
        this.subscriptions = response.data.getTheaterSubscriptions
      } catch (error) {
        console.error('Error fetching subscriptions:', error)
      } finally {
        this.loading = false
      }
    },
    formatSubscriberPurchase(purchase) {
      const subscription =
        this.getSubscriptionById(purchase.theaterSubscription.id) || purchase.theaterSubscription

      return {
        id: purchase.id,
        customerName: purchase.customerName,
        customerEmail: purchase.customerEmail,
        customerAddress: purchase.customerAddress,
        customerPhone: purchase.customerPhone,
        subscriptionName: subscription ? subscription.name : 'N/A',
        validFrom: subscription ? moment(subscription.valid_from).format('DD.MM.YYYY') : 'N/A',
        validTo: subscription ? moment(subscription.valid_to).format('DD.MM.YYYY') : 'N/A',
        ticketLocation: this.formatTicketLocation(purchase.ticketLocation),
        ticketLocationRaw: purchase.ticketLocation || '',
        theaterSubscriptionVenue:
          (purchase.theaterSubscription && purchase.theaterSubscription.venue) ||
          (subscription && subscription.venue) ||
          null,
        active: purchase.active,
        status: purchase.active ? 'Active' : 'Inactive',
        theaterSubscriptionId: purchase.theaterSubscription.id,
      }
    },
    async fetchSubscribersForAllSubscriptions() {
      await this.fetchSubscribers()
    },
    buildSearchVariables() {
      const variables = {
        organizationSlug: this.organizationSlug,
        pagination: {
          page: 1,
          per_page: 200,
          sortBy: 'createdAt',
          descending: true,
        },
      }

      if (this.selectedSeason) variables.season = this.selectedSeason

      const trimmedSearch = this.searchTerm ? this.searchTerm.trim() : ''
      if (trimmedSearch) {
        variables.searchTerm = trimmedSearch
      }

      const hasSubscriptionFilter =
        this.selectedSubscriptionId !== '' &&
        this.selectedSubscriptionId !== null &&
        this.selectedSubscriptionId !== undefined

      if (hasSubscriptionFilter) {
        variables.subscriptionId = parseInt(this.selectedSubscriptionId, 10)
      }

      return variables
    },
    scheduleSubscriberFetch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.fetchSubscribers()
      }, 400)
    },
    async fetchSubscribers() {
      if (!this.organizationSlug) {
        return
      }
      const requestId = ++this.subscriberRequestId
      this.loading = true
      try {
        const response = await this.$apollo.query({
          query: searchTheaterSubscriptionPurchases,
          variables: this.buildSearchVariables(),
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.subscriberRequestId) return
        const payload = response.data.searchTheaterSubscriptionPurchases || {}
        const rows = Array.isArray(payload.rows) ? payload.rows : []
        this.subscribers = rows
          .filter(purchase => purchase !== null && purchase !== undefined)
          .map(this.formatSubscriberPurchase)
      } catch (error) {
        if (requestId !== this.subscriberRequestId) return
        console.error('Error fetching subscribers:', error)
        this.subscribers = []
      } finally {
        if (requestId === this.subscriberRequestId) this.loading = false
      }
    },
    getSubscriptionById(id) {
      return this.subscriptions.find(sub => sub.id === id)
    },
    isGeneralAdmissionTicketLocation(ticketLocation) {
      return ticketLocation === THEATER_SUBSCRIPTION_GA_TICKET_LOCATION
    },
    venueHasSeatMap(venue) {
      return !!(venue && Array.isArray(venue.seats) && venue.seats.length && venue.predefinedSections)
    },
    isGeneralAdmissionSubscriber(subscriber) {
      if (!subscriber) return false
      if (this.isGeneralAdmissionTicketLocation(subscriber.ticketLocationRaw || subscriber.ticketLocation)) {
        return true
      }

      return subscriber.theaterSubscriptionVenue
        ? !this.venueHasSeatMap(subscriber.theaterSubscriptionVenue)
        : false
    },
    formatTicketLocation(ticketLocation) {
      return this.isGeneralAdmissionTicketLocation(ticketLocation)
        ? this.organizationText(THEATER_SUBSCRIPTION_GA_DISPLAY_LABEL)
        : ticketLocation || 'N/A'
    },
    async fetchDetailedSubscriptionEvents(purchaseId) {
      this.detailedEventsLoading = true
      this.detailedEventsError = null
      this.detailedSubscriptionEvents = []
      try {
        const { data, errors } = await this.$apollo.query({
          query: getTheaterSubscriptionEventsForPurchase,
          variables: {
            purchaseId: parseInt(purchaseId),
            organizationSlug: this.organizationSlug,
          },
          fetchPolicy: 'network-only',
        })

        if (errors && errors.length > 0) {
          console.error('GraphQL errors fetching detailed subscription events:', errors)
          this.detailedEventsError =
            errors.map(e => e.message).join('; ') || 'GraphQL query failed.'
          // Fallback to sample data on GraphQL error
          this.usePlaceholderEvents(purchaseId, 'GraphQL Error')
        } else if (data && data.getTheaterSubscriptionEventsForPurchase) {
          // If data exists and the specific field is there (even if empty array)
          this.detailedSubscriptionEvents = data.getTheaterSubscriptionEventsForPurchase
          if (this.detailedSubscriptionEvents.length === 0) {
            console.warn(
              'No events returned from backend for this purchase. Displaying empty state.',
            )
          }
        } else {
          // This case handles data being null or the specific field missing,
          // implying the query might not be implemented correctly backend-side.
          console.warn(
            'Data is null or getTheaterSubscriptionEventsForPurchase field is missing. Using placeholder data.',
          )
          this.detailedEventsError = 'Query not fully implemented or returned no data structure.' // More specific error for this case
          this.usePlaceholderEvents(purchaseId, 'Query Not Implemented')
        }
      } catch (error) {
        console.error('Error fetching detailed subscription events:', error)
        this.detailedEventsError = error.message || 'Failed to load event details.'
        // Fallback to sample data on catch error
        this.usePlaceholderEvents(purchaseId, 'Catch Error')
      } finally {
        this.detailedEventsLoading = false
      }
    },
    usePlaceholderEvents(purchaseId, errorType) {
      const eventNames = [`${errorType} Event Alpha`, `${errorType} Event Beta`]
      const statuses = ['active', 'canceled']
      const baseYear = new Date().getFullYear() + 1
      let sampleEvents = []
      for (let i = 0; i < 2; i++) {
        const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')
        const day = String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')
        sampleEvents.push({
          id: `placeholder-evt-${purchaseId}-${i}`,
          date: `${baseYear}-${month}-${day}T10:00:00Z`,
          name: `${this.selectedSubscriber.subscriptionName || 'Subscription'} / ${
            eventNames[i % eventNames.length]
          }`,
          status: statuses[i % statuses.length],
        })
      }
      this.detailedSubscriptionEvents = sampleEvents
      console.warn(
        `Using placeholder data for detailed subscription events due to: ${errorType}. Implement backend query.`,
      )
    },
    viewSubscriber(item) {
      this.selectedSubscriber = {
        ...item,
        ticketLocationDetails: this.formatTicketLocation(item.ticketLocationRaw || item.ticketLocation),
      }
      this.showViewModal = true
      this.currentTab = 'subscription-events-tab'

      // Ensure all event-related states are reset before fetching
      this.detailedSubscriptionEvents = []
      this.detailedEventsError = null
      this.detailedEventsLoading = true // Set loading true immediately

      if (item.id) {
        this.fetchDetailedSubscriptionEvents(item.id) // item.id is the purchaseId
      } else {
        this.detailedEventsError = 'Subscriber ID not found, cannot fetch events.'
        this.detailedSubscriptionEvents = [] // Ensure it's empty
        this.detailedEventsLoading = false // Stop loading if no ID
      }

      // Reset floor plan related states
      this.resetFloorPlanData()
    },
    deleteSubscriber(item) {
      this.subscriberToDelete = item
      this.showDeleteDialog = true
    },
    async confirmDelete() {
      if (!this.subscriberToDelete || !this.subscriberToDelete.id) return
      try {
        await this.$apollo.mutate({
          mutation: deleteTheaterSubscriptionPurchase,
          variables: {
            id: parseInt(this.subscriberToDelete.id),
            organizationSlug: this.organizationSlug,
          },
        })
        this.subscribers = this.subscribers.filter(
          subscriber => subscriber.id !== this.subscriberToDelete.id,
        )
        this.showDeleteDialog = false
        this.subscriberToDelete = null
      } catch (error) {
        console.error('Error deleting subscriber:', error)
      }
    },
    showReleaseSeatConfirmation(event) {
      if (this.isSelectedSubscriberGeneralAdmission) {
        return
      }
      this.releaseSeatEventName = event.name || 'this event'
      this.releaseSeatPurchaseId = event.purchaseId
      this.releaseSeatEventId = event.id
      this.showReleaseSeatDialog = true
    },
    async confirmReleaseSeat() {
      if (!this.releaseSeatPurchaseId || !this.releaseSeatEventId) return

      try {
        await this.releaseSeatForSubscriptionPurchaseEvent(
          this.releaseSeatPurchaseId,
          this.releaseSeatEventId,
        )
        this.showReleaseSeatDialog = false
        this.releaseSeatEventName = ''
        this.releaseSeatPurchaseId = null
        this.releaseSeatEventId = null
      } catch (error) {
        console.error('Error releasing seat:', error)
        // Error handling is already done in releaseSeatForSubscriptionPurchaseEvent method
      }
    },
    formatSubscriptionType(type) {
      const typeMap = {
        yearly: 'Yearly',
        yearly_discounted: 'Yearly Discounted',
        half_year_1: 'Half Year 1',
        half_year_1_discounted: 'Half Year 1 Discounted',
        half_year_2: 'Half Year 2',
        half_year_2_discounted: 'Half Year 2 Discounted',
      }

      return typeMap[type] || type
    },
    getStatusColor(status) {
      if (status === 'Active') return 'green'
      // This method seems unused or its logic is now within getEventStatusIcon
      // For simplicity, we'll rely on getEventStatusIcon for color if needed elsewhere
      // or refactor this if it has a distinct purpose.
      // const statusDetails = this.getEventStatusIcon(status, '', null); // Dummy call, needs rework

      return 'grey' // Default color
    },
    shouldShowStatusIcon(event) {
      if (!event || !event.interactionStatus) return false

      // Show icons for these important statuses (excluding SCANNED - it goes in action area)
      const statusesWithIcons = [
        this.SubscriptionEventInteractionStatus.MISSED,
        this.SubscriptionEventInteractionStatus.MISSED_UNTRACKABLE_SCAN,
        this.SubscriptionEventInteractionStatus.RELEASED_AND_AVAILABLE,
        this.SubscriptionEventInteractionStatus.RELEASED_AND_SOLD_TO_OTHER,
        this.SubscriptionEventInteractionStatus.SEAT_ON_HOLD,
        this.SubscriptionEventInteractionStatus.SEAT_NOT_AVAILABLE,
      ]

      // Also show icon if event is cancelled
      if (event.status === 'cancelled') return true

      return statusesWithIcons.includes(event.interactionStatus)
    },

    getEventStatusMessage(event) {
      if (!event) {
        return { message: 'Event information unavailable', style: 'color: grey;' }
      }

      // Handle cancelled events first
      if (event.status === 'cancelled') {
        return {
          message: 'Event has been cancelled',
          style: 'color: #d32f2f; font-weight: 500;',
        }
      }

      // Handle specific interaction statuses
      switch (event.interactionStatus) {
      case this.SubscriptionEventInteractionStatus.SCANNED:
        return {
          message:
              'Ticket scanned - attendance confirmed. Seat cannot be released to maintain audit trail.',
          style: 'color: #388e3c; font-weight: 500;',
        }

      case this.SubscriptionEventInteractionStatus.ATTENDING:
        if (this.canReleaseEventSeat(event)) {
          return {
            message: 'Seat reserved and ready for event. Can be released if needed.',
            style: 'color: #1976d2;',
          }
        } else if (this.isEventInThePast(event.date)) {
          return {
            message: 'Past event - seat cannot be released after event date.',
            style: 'color: #757575;',
          }
        } else {
          return {
            message: 'Seat reserved for upcoming event.',
            style: 'color: #1976d2;',
          }
        }

      case this.SubscriptionEventInteractionStatus.MISSED:
        return {
          message: 'Event was missed - no attendance recorded.',
          style: 'color: #f57c00; font-weight: 500;',
        }

      case this.SubscriptionEventInteractionStatus.RELEASED_AND_AVAILABLE:
        return {
          message: 'Seat has been released and is now available for other customers.',
          style: 'color: #757575;',
        }

      case this.SubscriptionEventInteractionStatus.RELEASED_AND_SOLD_TO_OTHER:
        return {
          message: 'Seat was released and has been sold to another customer.',
          style: 'color: #d32f2f;',
        }

      case this.SubscriptionEventInteractionStatus.SUBSCRIPTION_RESERVED:
        if (this.canReleaseEventSeat(event)) {
          return {
            message: 'Seat reserved through subscription. Release available until event date.',
            style: 'color: #1976d2;',
          }
        } else {
          return {
            message: 'Seat reserved through subscription.',
            style: 'color: #1976d2;',
          }
        }

      case this.SubscriptionEventInteractionStatus.SEAT_ON_HOLD:
        return {
          message: 'Seat is temporarily on hold - processing in progress.',
          style: 'color: #ff9800;',
        }

      case this.SubscriptionEventInteractionStatus.SEAT_NOT_AVAILABLE:
        return {
          message: 'Seat is not available for this event.',
          style: 'color: #757575;',
        }

      case this.SubscriptionEventInteractionStatus.MISSED_UNTRACKABLE_SCAN:
        return {
          message: 'Event was missed - scan status could not be tracked.',
          style: 'color: #f57c00;',
        }

      default:
        // Handle unknown statuses
        return {
          message: this.organizationMessage('organizationAll.event_status_value0_11a06ab5', 'Event status: {{value0}}', { value0: event.interactionStatus || this.organizationMessage('organizationAll.unknown_bc7819b3', 'Unknown') }),
          style: 'color: #757575;',
        }
      }
    },

    getEventStatusIcon(interactionStatusKey, overallEventStatus, eventDate) {
      const icons = {
        [SubscriptionEventInteractionStatus.EVENT_CANCELLED]: {
          icon: 'cancel',
          color: 'red darken-1',
          tooltip: 'Event Canceled',
        },
        [SubscriptionEventInteractionStatus.SCANNED]: {
          icon: 'check_circle',
          color: 'green darken-1',
          tooltip: 'Scanned',
        },
        [SubscriptionEventInteractionStatus.MISSED]: {
          icon: 'highlight_off',
          color: 'orange darken-1',
          tooltip: 'Missed',
        },
        [SubscriptionEventInteractionStatus.MISSED_UNTRACKABLE_SCAN]: {
          icon: 'help_outline',
          color: 'orange darken-2',
          tooltip: 'Missed (Scan status unknown)',
        },
        [SubscriptionEventInteractionStatus.RELEASED_AND_AVAILABLE]: {
          icon: 'event_seat',
          color: 'grey darken-1',
          tooltip: 'Seat Released and Available',
        },
        [SubscriptionEventInteractionStatus.SUBSCRIPTION_RESERVED]: {
          icon: 'bookmark',
          color: 'blue darken-1',
          tooltip: 'Seat Reserved',
        },
        [SubscriptionEventInteractionStatus.ATTENDING]: {
          icon: 'event_available',
          color: 'blue darken-1',
          tooltip: 'Attending - Seat Reserved',
        },
        [SubscriptionEventInteractionStatus.RELEASED_AND_SOLD_TO_OTHER]: {
          icon: 'event_seat',
          color: 'red darken-1',
          tooltip: 'Seat Sold to Another User',
        },
        [SubscriptionEventInteractionStatus.SEAT_ON_HOLD]: {
          icon: 'hourglass_empty',
          color: 'amber darken-1',
          tooltip: 'Seat on Hold',
        },
        [SubscriptionEventInteractionStatus.SEAT_NOT_AVAILABLE]: {
          icon: 'event_busy',
          color: 'grey darken-2',
          tooltip: 'Seat Not Available',
        },
      }

      // 1. Handle 'cancelled' first - this is a definitive event state.
      if (overallEventStatus === 'cancelled') {
        return icons[SubscriptionEventInteractionStatus.EVENT_CANCELLED]
      }

      // 2. Handle specific terminal/interactive states that should take precedence.
      // These describe a specific outcome or current non-generic state.
      const highPriorityInteractiveStatuses = [
        SubscriptionEventInteractionStatus.SCANNED,
        SubscriptionEventInteractionStatus.MISSED, // Specific outcome
        SubscriptionEventInteractionStatus.MISSED_UNTRACKABLE_SCAN, // Specific outcome for untrackable scans
        SubscriptionEventInteractionStatus.RELEASED_AND_SOLD_TO_OTHER, // Specific outcome
        SubscriptionEventInteractionStatus.SUBSCRIPTION_RESERVED, // Active future state
        SubscriptionEventInteractionStatus.ATTENDING, // Active future state
        SubscriptionEventInteractionStatus.RELEASED_AND_AVAILABLE, // Current state of the seat
        SubscriptionEventInteractionStatus.SEAT_ON_HOLD, // Current state of the seat
      ]

      if (highPriorityInteractiveStatuses.includes(interactionStatusKey)) {
        const details = icons[interactionStatusKey]
        if (details) return details
      }

      if (eventDate && this.isEventInThePast(eventDate)) {
        return { icon: 'history', color: 'grey darken-1', tooltip: 'Past Event' }
      }

      const iconDetails = icons[interactionStatusKey]
      if (iconDetails) {
        return iconDetails
      }

      console.warn(
        `Unknown event interaction status key: ${interactionStatusKey} for event date ${eventDate}. Using default SEAT_NOT_AVAILABLE icon.`,
      )

      return icons[SubscriptionEventInteractionStatus.SEAT_NOT_AVAILABLE]
    },
    formatEventDate(dateString) {
      if (!dateString) return 'N/A'

      return moment(dateString).format('DD.MM.YYYY')
    },
    async fetchFloorPlanData() {
      console.log(
        '[fetchFloorPlanData] Called. Selected Subscriber ID:',
        this.selectedSubscriber ? this.selectedSubscriber.id : 'N/A',
      )
      if (!this.selectedSubscriber || !this.selectedSubscriber.id) {
        this.floorPlanError = 'No subscriber selected to fetch floor plan for.'
        this.floorPlanData = null
        console.warn('[fetchFloorPlanData] No subscriber selected.')

        return
      }
      if (this.isGeneralAdmissionSubscriber(this.selectedSubscriber)) {
        this.floorPlanData = null
        this.floorPlanError = null
        this.floorPlanLoading = false

        return
      }

      this.floorPlanLoading = true
      this.floorPlanError = null
      this.floorPlanData = null // Clear previous data
      console.log('[fetchFloorPlanData] Set loading true, cleared error/data.')

      try {
        // Actual Apollo Query
        console.log(
          `[fetchFloorPlanData] Attempting actual GraphQL query for purchase ID: ${this.selectedSubscriber.id}, Org Slug: ${this.organizationSlug}`,
        )
        const { data, errors } = await this.$apollo.query({
          // also log errors from query
          query: getFloorPlanForSubscriptionPurchase, // Make sure this is imported
          variables: {
            purchaseId: parseInt(this.selectedSubscriber.id), // Ensure purchaseId is an Int
            organizationSlug: this.organizationSlug,
          },
          fetchPolicy: 'network-only',
        })

        console.log('[fetchFloorPlanData] GraphQL response data:', data)
        if (errors) {
          console.error('[fetchFloorPlanData] GraphQL errors:', errors)
          this.floorPlanError =
            errors.map(e => e.message).join('; ') || 'GraphQL query returned errors.'
          this.floorPlanData = null

          return // Stop further processing if GraphQL itself errors
        }

        if (data && data.getFloorPlanForSubscriptionPurchase) {
          this.floorPlanData = data.getFloorPlanForSubscriptionPurchase
          if (!this.floorPlanData.venueImageUrl || !this.floorPlanData.svgSeatId) {
            console.warn(
              '[fetchFloorPlanData] Received data but venueImageUrl or svgSeatId is missing:',
              this.floorPlanData,
            )
            this.floorPlanError = 'Floor plan details incomplete from server.'
            // this.floorPlanData = null; // Decide if partial data should clear everything
          }
        } else {
          this.floorPlanError = 'Floor plan data not found in GraphQL response.'
          this.floorPlanData = null
          console.warn(
            '[fetchFloorPlanData] getFloorPlanForSubscriptionPurchase was null or undefined in response data.',
          )
        }
      } catch (error) {
        console.error('Error fetching floor plan data:', error)
        this.floorPlanError =
          error.message || 'An unexpected error occurred while fetching the floor plan.'
        this.floorPlanData = null
      } finally {
        this.floorPlanLoading = false
      }
    },

    onFloorPlanSvgLoad() {
      this.applyFloorPlanZoom() // Apply initial zoom
      if (!this.floorPlanData || !this.floorPlanData.svgSeatId) {
        console.warn(
          '[onFloorPlanSvgLoad] SVG loaded, but no seat ID to highlight. floorPlanData:',
          JSON.parse(JSON.stringify(this.floorPlanData)),
        )

        return
      }
      try {
        const svgObject = this.$refs.subscriberFloorPlanSvgObject
        if (!svgObject || !svgObject.contentDocument) {
          console.error('[onFloorPlanSvgLoad] SVG object or contentDocument not found.')
          this.floorPlanError = 'Could not access SVG content to highlight seat.'

          return
        }
        const svgDoc = svgObject.contentDocument
        const seatElement = svgDoc.getElementById(this.floorPlanData.svgSeatId)

        if (seatElement) {
          console.log('Highlighting seat:', this.floorPlanData.svgSeatId, seatElement)
          seatElement.style.fill = 'rgba(0, 0, 255, 0.7)' // Example: Blue fill
          seatElement.style.stroke = 'darkblue'
          seatElement.style.strokeWidth = '2px' // Ensure strokeWidth is a string with units if needed by SVG
        } else {
          console.warn(`Seat ID "${this.floorPlanData.svgSeatId}" not found in SVG.`)
          // Optionally inform user: this.floorPlanError = `Seat ID "${this.floorPlanData.svgSeatId}" could not be found on the plan.`;
        }
      } catch (error) {
        console.error('Error highlighting seat in SVG:', error)
        this.floorPlanError = 'Failed to highlight seat on the floor plan.'
      }
    },

    zoomFloorPlanIn() {
      if (this.floorPlanZoomLevel < 2.5) {
        this.floorPlanZoomLevel += 0.2
        this.applyFloorPlanZoom()
      }
    },

    zoomFloorPlanOut() {
      if (this.floorPlanZoomLevel > 0.5) {
        this.floorPlanZoomLevel -= 0.2
        this.applyFloorPlanZoom()
      }
    },

    applyFloorPlanZoom() {
      this.$nextTick(() => {
        // Ensure DOM is updated
        const svgObject = this.$refs.subscriberFloorPlanSvgObject
        if (svgObject) {
          // The object element itself should not be scaled, but its content.
          // However, direct scaling of SVG content from outside can be tricky.
          // Let's try scaling the wrapper or the object, common approaches.
          // If the SVG content is a single <svg> root, scaling that is best.
          const wrapper = this.$refs.floorPlanObjectWrapper
          if (wrapper) {
            wrapper.style.transform = `scale(${this.floorPlanZoomLevel})`
            wrapper.style.transformOrigin = 'center center' // Or 'top left' depending on desired behavior
          }
        }
      })
    },
    async releaseSeatForSubscriptionPurchaseEvent(purchaseId, eventId) {
      if (this.isSelectedSubscriberGeneralAdmission) {
        return
      }
      try {
        await this.$apollo.mutate({
          mutation: releaseSeatForSubscriptionPurchaseEvent, // Imported GQL Mutation
          variables: {
            purchaseId: parseInt(purchaseId),
            eventId: parseInt(eventId),
          },
        })
        // Refetch to confirm and get authoritative data
        // this.selectedSubscriber.id should be the purchaseId for the currently viewed subscriber
        await this.fetchDetailedSubscriptionEvents(this.selectedSubscriber.id)

        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: 'Seat released successfully.',
            color: 'success',
          })
        }
      } catch (error) {
        console.error('Error releasing seat:', error)
        // No optimistic update to revert, but still show error to user
        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: this.organizationMessage('organizationAll.failed_to_release_seat_value0_11a21319', 'Failed to release seat: {{value0}}', { value0: error.message || this.organizationMessage('organizationAll.unknown_error_e5fd9aa2', 'Unknown error') }),
            color: 'error',
          })
        }
        // Optionally, you might still want to refetch even on error to ensure UI consistency
        // if the error was not related to the actual seat state (e.g. network error before mutation execution)
        // await this.fetchDetailedSubscriptionEvents(this.selectedSubscriber.id);
      }
    },
    async releaseSeatForAllUpcomingPerformances(purchaseId) {
      if (this.isSelectedSubscriberGeneralAdmission) {
        return
      }
      try {
        await this.$apollo.mutate({
          mutation: releaseSeatForAllUpcomingPerformances,
          variables: {
            theaterSubscriptionPurchaseId: parseInt(purchaseId),
          },
        })
        await this.fetchDetailedSubscriptionEvents(this.selectedSubscriber.id)
      } catch (error) {
        console.error('Error releasing seat:', error)
      }
    },

    async reassignSeat(purchaseId, eventId) {
      try {
        await this.$apollo.mutate({
          mutation: reassignSeatForSubscriptionPurchaseEvent,
          variables: {
            purchaseId: parseInt(purchaseId),
            eventId: parseInt(eventId),
          },
        })
        await this.fetchDetailedSubscriptionEvents(this.selectedSubscriber.id)
        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: 'Seat reassigned successfully.',
            color: 'success',
          })
        }
      } catch (error) {
        console.error('Error reassigning seat:', error)
        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: this.organizationMessage('organizationAll.failed_to_reassign_seat_value0_3a42704f', 'Failed to reassign seat: {{value0}}', { value0: error.message || this.organizationMessage('organizationAll.unknown_error_e5fd9aa2', 'Unknown error') }),
            color: 'error',
          })
        }
        // Optionally refetch even on error
        // await this.fetchDetailedSubscriptionEvents(this.selectedSubscriber.id);
      }
    },
    async changeSeat() {
      // await this.$apollo.mutate({
      //   mutation: changeSeatForSubscriptionPurchaseEvent,
      //   variables: {
      //     purchaseId: parseInt(this.selectedSubscriber.id),
      //     eventId: parseInt(this.selectedEvent.id),
      //     ticketLocation: this.selectedTicketLocation,
      //   },
      // })
    },
    resetFloorPlanData() {
      this.floorPlanData = null
      this.floorPlanLoading = false
      this.floorPlanError = null
      this.floorPlanZoomLevel = 1
      // Reset any transformations if needed
      const wrapper = this.$refs.floorPlanObjectWrapper
      if (wrapper) {
        wrapper.style.transform = 'scale(1)'
      }
    },
    isEventInThePast(eventDate) {
      if (!eventDate) return true // Treat events without a date as past for safety

      return moment(eventDate).isBefore(moment(), 'day')
    },
    canReleaseEventSeat(event) {
      if (this.isSelectedSubscriberGeneralAdmission) {
        return false
      }
      if (!event || !event.subscriberSeatState) {
        return false
      }

      // Cannot release seat if user has already scanned in (attended the event)
      if (event.interactionStatus === this.SubscriptionEventInteractionStatus.SCANNED) {
        return false
      }

      // Cannot release seat if it's already been released and sold to someone else
      if (
        event.interactionStatus ===
        this.SubscriptionEventInteractionStatus.RELEASED_AND_SOLD_TO_OTHER
      ) {
        return false
      }

      // Cannot release seat for missed events
      if (
        event.interactionStatus === this.SubscriptionEventInteractionStatus.MISSED ||
        event.interactionStatus === this.SubscriptionEventInteractionStatus.MISSED_UNTRACKABLE_SCAN
      ) {
        return false
      }

      // Seat must be SOLD to be releasable (not already available)
      const isSold = event.subscriberSeatState === this.SeatState.SOLD

      const isFutureEvent = !this.isEventInThePast(event.date)

      const validInteractionStatuses = [
        this.SubscriptionEventInteractionStatus.ATTENDING,
        // this.SubscriptionEventInteractionStatus.SCANNED, // Removed - scanned events cannot be released
        // this.SubscriptionEventInteractionStatus.MISSED, // Removed - missed events cannot be released
        this.SubscriptionEventInteractionStatus.SUBSCRIPTION_RESERVED,
      ]
      const hasValidInteractionStatus = validInteractionStatuses.includes(event.interactionStatus)

      return isSold && isFutureEvent && hasValidInteractionStatus
    },

    isEventSeatReleasedAndSoldToOther(event) {
      if (!event) return false

      return (
        event.interactionStatus ===
        this.SubscriptionEventInteractionStatus.RELEASED_AND_SOLD_TO_OTHER
      )
    },

    canReassignEventSeat(event) {
      if (this.isSelectedSubscriberGeneralAdmission) {
        return false
      }
      if (!event) return false

      // Cannot reassign missed events
      if (
        event.interactionStatus === this.SubscriptionEventInteractionStatus.MISSED ||
        event.interactionStatus === this.SubscriptionEventInteractionStatus.MISSED_UNTRACKABLE_SCAN
      ) {
        return false
      }

      // Cannot reassign past events
      if (this.isEventInThePast(event.date)) {
        return false
      }

      return (
        event.status === 'active' &&
        event.interactionStatus === this.SubscriptionEventInteractionStatus.RELEASED_AND_AVAILABLE
      )
    },
    async printSubscription(purchaseId) {
      this.printingSubscription = true
      try {
        // Use the same endpoint pattern as tickets, but without blob responseType
        const lang = 'hr' // Default to Croatian, could be made configurable
        const { data: subscription } = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/files/theater-subscription/${purchaseId}?lang=${lang}`,
        )

        // Decode base64 response like in SuccessfulPayment.vue
        const byteString = window.atob(subscription.body)
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const int8Array = new Uint8Array(arrayBuffer)
        for (let i = 0; i < byteString.length; i++) {
          int8Array[i] = byteString.charCodeAt(i)
        }
        const blob = new Blob([int8Array], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)

        // Create a temporary link element and trigger download
        const downloadLink = document.createElement('a')
        downloadLink.href = url
        downloadLink.setAttribute('download', `CoreEvent-Subscription-${purchaseId}.pdf`)
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)

        // Clean up
        URL.revokeObjectURL(url)

        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: 'Subscription PDF downloaded successfully.',
            color: 'success',
          })
        }
      } catch (error) {
        console.error('Error downloading subscription PDF:', error)
        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: this.organizationMessage('organizationAll.failed_to_download_subscription_pdf_value0_f0b9cb04', 'Failed to download subscription PDF: {{value0}}', { value0: error.message || this.organizationMessage('organizationAll.unknown_error_e5fd9aa2', 'Unknown error') }),
            color: 'error',
          })
        }
      } finally {
        this.printingSubscription = false
      }
    },
    async resendSubscriptionEmail(purchaseId) {
      this.resendingEmail = true
      try {
        const lang = 'hr' // Default to Croatian, could be made configurable
        await this.$apollo.mutate({
          mutation: resendTheaterSubscriptionConfirmation,
          variables: {
            purchaseId: parseInt(purchaseId),
            organizationSlug: this.organizationSlug,
            lang,
          },
        })

        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: 'Subscription confirmation email sent successfully.',
            color: 'success',
          })
        }
      } catch (error) {
        console.error('Error resending subscription email:', error)
        if (this.$root && this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: this.organizationMessage('organizationAll.failed_to_resend_subscription_email_value0_61b941cc', 'Failed to resend subscription email: {{value0}}', { value0: error.message || this.organizationMessage('organizationAll.unknown_error_e5fd9aa2', 'Unknown error') }),
            color: 'error',
          })
        }
      } finally {
        this.resendingEmail = false
      }
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important; /* left-aligned like membership list */
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 0.5rem;
}
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
  margin-bottom: 1rem;
}

/* Align table look and feel with promo/membership lists */
.compact-table {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}
/* Deep selectors to reach Vuetify table internals */
.compact-table >>> thead tr th {
  padding: 12px 16px !important;
  border-bottom: 1px solid #e0e0e0 !important;
}
.compact-table >>> tbody tr td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #f0f0f0;
}
.compact-table >>> tbody tr:last-child td {
  border-bottom: 0;
}
.compact-table >>> tbody tr:hover {
  background: #fafafa;
}
.v-btn:hover::before {
  background-color: transparent !important;
}
.card {
  margin-bottom: 20px;
}
.detail-row {
  display: flex;
  margin-bottom: 12px;
}
.detail-label {
  font-weight: bold;
  min-width: 120px;
}
.detail-value {
  flex: 1;
}

/* Styles for the new modal */
.subscriber-detail-modal .v-card__title {
  padding-bottom: 4px;
}

.subscriber-detail-modal .v-tabs-bar {
  padding-left: 16px;
  padding-right: 16px;
}
.subscriber-detail-modal .v-tab {
  font-size: 0.875rem; /* 14px */
  letter-spacing: 0.05em;
}

.subscriber-name {
  font-family: Raleway;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -3%;
  color: #333333;
}

.close-modal-btn {
  max-width: fit-content;
}
.close-modal-btn:hover .close-modal-btn::before {
  background-color: transparent !important;
}

.subscription-details-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px;
}
.event-list-item {
  min-height: 56px;
  padding: 0; /* Replaces px-0 class for v-list-tile */
}

.event-list-item .v-list__tile {
  padding-left: 0;
  padding-right: 0;
}

.event-date {
  display: inline-block;
  width: 80px;
  font-size: 0.875rem;
}

.event-name {
  font-weight: 500;
  font-size: 0.875rem;
}
.tabs-container {
  background-color: transparent;
  width: 100%;
  display: flex;
  padding: 0 24px;
}
:deep(.v-tabs__wrapper),
:deep(.v-tabs__bar) {
  width: 100%;
}
.tab-container:deep(.v-tabs__item) {
  width: fit-content;
  background-color: #f5f5f5;
  color: #0277bd;
  font-weight: 700;
  border-radius: 8px;
  color: var(--primary, #0277bd);
  font-family: Raleway;
  font-weight: 700;
  font-size: 18px;
  line-height: 120%;
  letter-spacing: -1.5%;
  text-align: center;
  text-transform: uppercase;
}
.tab-container:deep(.v-tabs__item--active) {
  background-color: #0277bd;
  color: white;
}
.release-seats-btn-container {
  margin-left: auto;
}
.release-seats-btn {
  background-color: #e6f1f8;
  color: var(--primary, #0277bd);
  font-family: Raleway;
  font-weight: 700;
  font-size: 18px;
  line-height: 120%;
  letter-spacing: -1.5%;
  text-align: center;
  text-transform: uppercase;
}
.release-seat-btn {
  font-family: Raleway;
  font-weight: 700;
  font-size: 18px;
  line-height: 120%;
  letter-spacing: -1.5%;
  text-align: center;
  color: #0277bd;
  text-transform: uppercase;
}
.empty-state-content .v-icon {
  opacity: 0.5;
}

.floor-plan-container {
  min-height: 300px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.floor-plan-container img {
  border-radius: 4px;
}

.subscriber-detail-modal .v-card__actions {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.modal-card-text-content {
  min-height: 250px;
}

/* Styles for Floor Plan Tab */
.floor-plan-tab-content {
  /* Similar to .modal-card-text-content or define new min-height */
  min-height: 350px; /* Adjust as needed */
  display: flex;
  flex-direction: column;
}

.floor-plan-display-container {
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column; /* If zoom controls are part of this flex flow */
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden; /* Crucial for containing the scaled SVG */
  background-color: transparent; /* Changed for debugging */
}

.floor-plan-object-wrapper {
  flex-grow: 1;
  display: flex; /* For centering the object if it's smaller than wrapper */
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  transition: transform 0.2s ease-out; /* Smooth zoom */
  /* overflow: hidden; */ /* Temporarily commented for debugging */
}

/* Ensure the object itself doesn't add extra space if its default display is inline */
.floor-plan-object-wrapper object {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain; /* Or 'scale-down' if you want it to shrink but not grow beyond original */
}

.zoom-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  padding: 4px;
  display: flex;
  flex-direction: column;
}
.zoom-controls .v-btn {
  min-width: auto; /* Vuetify 1.x might need this for icon buttons */
}

/* Action button styles to match project standards */
.action-button {
  border-radius: 24px !important;
  text-transform: uppercase !important;
  font-weight: 700 !important;
  font-family: 'Raleway', sans-serif !important;
  font-size: 14px !important;
  letter-spacing: 0.5px !important;
  color: white !important;
  background: #0277bd !important;
  transition: background-color 0.3s ease !important;
}

.action-button:hover {
  background: #004c8c !important;
}

.action-button:disabled {
  background: #cccccc !important;
  color: #666666 !important;
}

.action-button-outline {
  border-radius: 24px !important;
  text-transform: uppercase !important;
  font-weight: 700 !important;
  font-family: 'Raleway', sans-serif !important;
  font-size: 14px !important;
  letter-spacing: 0.5px !important;
  color: #0277bd !important;
  border: 2px solid #0277bd !important;
  background: transparent !important;
  transition: all 0.3s ease !important;
}

.action-button-outline:hover {
  background: #0277bd !important;
  color: white !important;
}

.action-button-outline:disabled {
  border-color: #cccccc !important;
  color: #666666 !important;
}
.subscriber-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
}

.export-link {
  text-decoration: none;
}
.row-email-action {
  color: #0277bd;
  cursor: pointer;
  font: 400 14px 'Source Sans Pro', sans-serif;
  white-space: nowrap;
}
.row-email-action:disabled {
  color: #979797;
  cursor: not-allowed;
}
</style>
